import { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from 'components/common/Modal.jsx';
import Button from 'components/common/Button.jsx';
import Input from 'components/common/Input.jsx';
import { ConfirmContext } from './dialogsContext.js';

/**
 * Themed replacements for window.confirm / window.prompt / window.alert.
 *
 * public/index.html makes 50 native dialog calls. They block the event loop,
 * they cannot be styled, and they look like browser chrome in all three themes —
 * jarring in V2 and V3 especially, where nothing else on screen is system UI.
 *
 * Usage reads like the native call it replaces:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm('Pause 12 campaigns?'))) return;
 *
 * Resolves false / null when dismissed, matching confirm() and prompt(), so
 * ported call sites keep their existing guard clauses.
 *
 * ── WHY alert() IS A TOAST AND THE OTHER TWO ARE NOT ────────────────────────
 * alert() only ever REPORTS — "Enter an amount", "Could not add BM". Every one
 * of its ~30 call sites is a validation failure or a failed operation, none
 * carries a title, and none needs an answer. A modal for that demands a click
 * to dismiss something the user cannot act on inside the dialog anyway, and
 * stamps a meaningless "Notice" heading above one line of text.
 *
 * confirm() and prompt() STAY modals, and must: one needs a yes/no back and the
 * other needs typed input. A toast cannot block for either, and turning them
 * into toasts would mean a pause or a budget change happening with no way to
 * say no.
 *
 * alert() keeps its exact signature — it still returns a Promise — so all its
 * call sites are unchanged and `await alert(…)` still reads the same. It simply
 * resolves immediately now rather than when someone clicks OK.
 *
 * notice() is the fourth shape: a MODAL that only informs. It exists for the
 * explanations a toast cannot carry — the red dot's "why is this row marked",
 * several paragraphs someone stopped to read — where a six-second toast would
 * vanish mid-sentence. One Close button, no question asked, resolves true.
 *
 * The context object and the hooks live in ./dialogsContext.js — deliberately
 * away from these UI imports; that file says why. They are re-exported here so
 * every existing call site keeps its one import.
 */

const EMPTY = { open: false, kind: 'confirm', message: '', title: '', confirmLabel: '', danger: false, defaultValue: '', requireText: '' };

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(EMPTY);
  const [value, setValue] = useState('');
  const resolver = useRef(null);

  const settle = useCallback((result) => {
    setDialog(EMPTY);
    const done = resolver.current;
    resolver.current = null;
    if (done) done(result);
  }, []);

  const open = useCallback((kind, message, opts = {}) => {
    setValue(opts.defaultValue ?? '');
    setDialog({
      open: true,
      kind,
      message,
      title: opts.title ?? '',
      confirmLabel: opts.confirmLabel ?? 'Confirm',
      danger: Boolean(opts.danger),
      defaultValue: opts.defaultValue ?? '',
      /*
       * A prompt whose Confirm stays dead until this exact string is typed.
       *
       * For deletes. A dialog you can dismiss with the Enter key is no friction
       * at all, and the difference between "I meant to pause that" and "I meant
       * to delete that" is the difference between a bad afternoon and an
       * unrecoverable one. Typing the title is the smallest thing that makes
       * the click deliberate.
       */
      requireText: opts.requireText ?? '',
    });
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const confirm = useCallback((message, opts) => open('confirm', message, opts), [open]);
  const prompt = useCallback((message, opts) => open('prompt', message, opts), [open]);
  const notice = useCallback((message, opts = {}) => open('notice', message, { confirmLabel: 'Close', ...opts }), [open]);

  /**
   * Report something. A toast, not a modal — see the note at the top.
   *
   * `kind` defaults to 'error' because that is what every existing caller is
   * doing: reporting a validation failure or an operation that did not happen.
   * Pass 'success' for the opposite, or 'info' for something merely worth
   * knowing.
   *
   * Still returns a Promise so `await alert(…)` keeps working everywhere.
   */
  const alert = useCallback((message, opts = {}) => {
    const text = String(message ?? '');
    const kind = opts.kind ?? 'error';
    const fire = kind === 'success' ? toast.success : kind === 'info' ? toast : toast.error;
    fire(text);
    return Promise.resolve(true);
  }, []);

  const value_ = useMemo(() => ({ confirm, prompt, alert, notice }), [confirm, prompt, alert, notice]);

  // Dismissing is a negative answer, not an empty one — prompt() returns null.
  const onCancel = () => settle(dialog.kind === 'prompt' ? null : false);
  const matched = !dialog.requireText || value.trim() === dialog.requireText;
  const onAccept = () => {
    if (!matched) return;
    settle(dialog.kind === 'prompt' ? value : true);
  };

  return (
    <ConfirmContext.Provider value={value_}>
      {children}
      {/* confirm(), prompt() and notice() — alert() is a toast. A notice asks
          nothing, so it offers no Cancel: one Close button, resolving true. */}
      <Modal
        open={dialog.open}
        title={dialog.title || (dialog.kind === 'notice' ? 'Details' : 'Are you sure?')}
        onClose={onCancel}
        width={440}
        footer={
          dialog.kind === 'notice' ? (
            <Button variant='primary' onClick={onAccept} autoFocus>
              {dialog.confirmLabel}
            </Button>
          ) : (
            <>
              <Button variant='outline' onClick={onCancel}>
                Cancel
              </Button>
              <Button variant={dialog.danger ? 'red' : 'primary'} disabled={!matched} onClick={onAccept} autoFocus>
                {dialog.confirmLabel}
              </Button>
            </>
          )
        }
      >
        <p className='modal__message'>{dialog.message}</p>
        {dialog.kind === 'prompt' && (
          <div className='modal__prompt'>
            {dialog.requireText && (
              <div className='modal__require'>
                Type <code>{dialog.requireText}</code> to confirm
              </div>
            )}
            <Input
              value={value}
              autoFocus
              placeholder={dialog.requireText || undefined}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onAccept();
              }}
            />
          </div>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export { useConfirm, useDialogs } from './dialogsContext.js';
